import {
  ClosedContestWithWinners,
  ContestInformation,
  ContestToRegister,
  ContestType,
  ContestWithUserAccount,
  LeaderboardAccount,
  PaginateLeaderboardOptions,
  PaginationResult,
  UserContestRanking,
} from '@monorepo/types';
import BaseEntityService, { BaseResponse } from '../core/base-entity-service';

export default class ContestService extends BaseEntityService {
  static override route = 'contest';

  async getContestWithAccount(contestId: string, influencerCode?: string) {
    try {
      const params = influencerCode ? { code: influencerCode } : undefined;
      const response = await this.httpService.get<
        BaseResponse<ContestWithUserAccount>
      >(`${this.path}/${contestId}`, { params });

      return response.data;
    } catch (e) {
      console.error(`Failed getting contest details: ${contestId}`, e);
      throw e;
    }
  }

  async getLeaderboard(
    contestId: string,
    options?: PaginateLeaderboardOptions,
  ) {
    try {
      const response = await this.httpService.get<
        BaseResponse<LeaderboardAccount[]>
      >(`${this.path}/leaderboard/${contestId}`, {
        params: options,
      });

      return response.data;
    } catch (e) {
      console.error(`Failed getting contest leaderboard: ${contestId}`, e);
      throw e;
    }
  }

  async getOldLeaderboard(
    contestId: string,
    options?: PaginateLeaderboardOptions,
  ) {
    try {
      const response = await this.httpService.get<
        BaseResponse<LeaderboardAccount[]>
      >(`${this.path}/old-leaderboard/${contestId}`, {
        params: options,
      });

      return response.data;
    } catch (e) {
      console.error(`Failed getting contest leaderboard: ${contestId}`, e);
      throw e;
    }
  }

  async getContestToRegister(): Promise<ContestType | undefined> {
    try {
      const response = await this.httpService.get<
        BaseResponse<ContestToRegister>
      >(`${this.path}/to-register`);

      return response.data.contest;
    } catch (e) {
      console.error(`Failed getting contest to register`, e);
      throw e;
    }
  }

  async registerToContest(contestId: string, influencerCode?: string) {
    try {
      const body = influencerCode ? { code: influencerCode } : {};
      const response = await this.httpService.post<
        { code?: string },
        BaseResponse<ContestType>
      >(`${this.path}/register/${contestId}`, body);

      return response.data;
    } catch (e) {
      console.error(`Failed registering to contest: ${contestId}`, e);
      throw e;
    }
  }

  async getContestInformation() {
    try {
      const response = await this.httpService.get<
        BaseResponse<ContestInformation>
      >(`${this.path}/category/information`);

      return response.data;
    } catch (e) {
      console.error(`Failed getting contest information`, e);
      throw e;
    }
  }

  async pinUser(contestId: string, targetPublicId: string) {
    try {
      const response = await this.httpService.post<
        { targetPublicId: string },
        BaseResponse<{ success: boolean }>
      >(`${this.path}/${contestId}/pin-user`, { targetPublicId });

      return response.data;
    } catch (e) {
      console.error(`Failed pinning user in contest: ${contestId}`, e);
      throw e;
    }
  }

  async getClosedContestWithWinners(page?: number, limit?: number) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<ClosedContestWithWinners>>
      >(`${this.path}/category/closed/winners`, {
        params: page && limit ? { page, limit } : undefined,
      });

      return response.data;
    } catch (e) {
      console.error(`Failed getting closed contest with winners`, e);
      throw e;
    }
  }

  async unpinUser(contestId: string, targetPublicId: string) {
    try {
      const response = await this.httpService.post<
        { targetPublicId: string },
        BaseResponse<{ success: boolean }>
      >(`${this.path}/${contestId}/unpin-user`, { targetPublicId });

      return response.data;
    } catch (e) {
      console.error(`Failed unpinning user in contest: ${contestId}`, e);
      throw e;
    }
  }

  async getPinnedLeaderboardAccounts(contestId: string) {
    try {
      const response = await this.httpService.get<
        BaseResponse<LeaderboardAccount[]>
      >(`${this.path}/leaderboard/pinned/${contestId}`);

      return response.data;
    } catch (e) {
      console.error(
        `Failed getting pinned leaderboard accounts: ${contestId}`,
        e,
      );
      throw e;
    }
  }

  async getCurrentUserLeaderboardAccount(contestId: string) {
    try {
      const response = await this.httpService.get<
        BaseResponse<LeaderboardAccount | null>
      >(`${this.path}/leaderboard/currentUser/${contestId}`);

      return response.data;
    } catch (e) {
      console.error(
        `Failed getting current user leaderboard account: ${contestId}`,
        e,
      );
      throw e;
    }
  }

  async getUserContestRanking() {
    try {
      const response = await this.httpService.get<
        BaseResponse<UserContestRanking[]>
      >(`${this.path}/category/user-ranking`);

      return response.data;
    } catch (e) {
      console.error(`Failed getting user contest ranking`, e);
      throw e;
    }
  }

  async validateInfluencerCode(code: string) {
    try {
      const response = await this.httpService.post<
        { code: string },
        BaseResponse<ContestWithUserAccount>
      >(`${this.path}/validate-influencer-code`, { code });

      return response.data;
    } catch (e) {
      console.error(`Failed validating influencer code: ${code}`, e);
      throw e;
    }
  }
}
